import { EntityType } from '../../../types.generated';

/** Entity action keys (must match ENTITY_ACTIONS; used with ENTITY_TYPE_PRIVILEGE_MAP). */
export type EntityMenuAction =
    | 'CLONE'
    | 'VIEW'
    | 'CREATE'
    | 'UPDATE'
    | 'DELETE'
    | 'EXECUTE'
    | 'CANCEL'
    | 'UPDATE_GOV_STATUS'
    | 'UPDATE_GOV_FLAG_STATUS'
    | 'EDIT_READONLY'
    | 'EDIT_DEPRECATION'
    | 'UPDATE_PARENT'
    | 'EXPORT'
    | 'EDIT_NAME'
    | 'ADD_TERM'
    | 'ADD_TERM_GROUP'
    | 'BULK_IMPORT'
    | 'ADD_RELATED_TERMS'
    | 'ADD_RELATED_NODES'
    | 'REMOVE_RELATED_TERMS'
    | 'REMOVE_RELATED_NODES'
    | 'UPDATE_SUBTYPE'
    | 'LINEAGE'
    | 'MANAGE_CUSTOM_RELATIONSHIP'
    | 'CREATE_CUSTOM_RELATIONSHIP'
    | 'UPDATE_CUSTOM_RELATIONSHIP'
    | 'DELETE_CUSTOM_RELATIONSHIP';

export const SECRETS_ENTITY_TYPE = 'SECRET';
export const OWNERSHIP_TYPE_ENTITY_TYPE = 'OWNERSHIP_TYPE';

// PLATFORM PRIVILEGES
// This is top level privileges that allow every thing in the platform to be managed
export const MANAGE_PLATFORM_PRIVILEGE_MAP: Partial<
    Record<EntityType | typeof SECRETS_ENTITY_TYPE | typeof OWNERSHIP_TYPE_ENTITY_TYPE, string>
> = {
    [EntityType.Dataset]: 'manageDatasets',
    [EntityType.Query]: 'manageDatasetsQuery',
    [EntityType.Dashboard]: 'manageDashboards',
    [EntityType.Chart]: 'manageCharts',
    [EntityType.DataJob]: 'manageDataJobs',
    [EntityType.DataFlow]: 'manageDataFlows',
    [EntityType.DataProduct]: 'manageDataProducts',
    [EntityType.Tag]: 'manageTags',
    [EntityType.Domain]: 'manageDomains',
    [EntityType.GlossaryTerm]: 'manageGlossaryTerms',
    [EntityType.GlossaryNode]: 'manageGlossaryNodes',
    [EntityType.Container]: 'manageContainers',
    [EntityType.IngestionSource]: 'manageIngestion',
    [EntityType.CorpUser]: 'manageUsers',
    [EntityType.Mlmodel]: 'manageMLModel',
    [EntityType.MlmodelGroup]: 'manageMLModelGroup',
    [EntityType.Mlfeature]: 'manageMLFeature',
    [EntityType.MlfeatureTable]: 'manageMLFeatureTable',
    [EntityType.MlprimaryKey]: 'manageMLPrimaryKey',
    [EntityType.Notebook]: 'manageNotebook',
    [EntityType.DataProcessInstance]: 'manageDataProcessInstance',
    [SECRETS_ENTITY_TYPE]: 'manageSecrets',
    [OWNERSHIP_TYPE_ENTITY_TYPE]: 'manageOwnershipTypes',
};

export const VIEW_PLATFORM_PRIVILEGE_MAP: Partial<
    Record<EntityType | typeof SECRETS_ENTITY_TYPE | typeof OWNERSHIP_TYPE_ENTITY_TYPE, string>
> = {
    [EntityType.Dataset]: 'viewDataset',
    [EntityType.Dashboard]: 'viewDashboard',
    [EntityType.Chart]: 'viewChart',
    [EntityType.DataJob]: 'viewDataJob',
    [EntityType.DataFlow]: 'viewDataFlow',
    [EntityType.DataProduct]: 'viewDataProduct',
    [EntityType.Tag]: 'viewTag',
    [EntityType.Domain]: 'viewDomain',
    [EntityType.GlossaryTerm]: 'viewGlossaryTerm',
    [EntityType.GlossaryNode]: 'viewGlossaryNode',
    [EntityType.Container]: 'viewContainer',
    [EntityType.IngestionSource]: 'viewIngestion',
    [EntityType.Mlmodel]: 'viewMLModel',
    [EntityType.MlmodelGroup]: 'viewMLModelGroup',
    [EntityType.Mlfeature]: 'viewMLFeature',
    [EntityType.MlfeatureTable]: 'viewMLFeatureTable',
    [EntityType.MlprimaryKey]: 'viewMLPrimaryKey',
    [EntityType.Notebook]: 'viewNotebook',
    [EntityType.DataProcessInstance]: 'viewDataProcessInstance',
    [SECRETS_ENTITY_TYPE]: 'viewSecrets',
    [OWNERSHIP_TYPE_ENTITY_TYPE]: 'viewOwnershipTypes',
};

export const BULK_IMPORT_PLATFORM_PRIVILEGE_MAP = {
    [EntityType.Dataset]: 'bulkDatasetImport',
    [EntityType.DataProduct]: 'bulkDataProductImport',
    [EntityType.GlossaryTerm]: 'bulkGlossaryImport',
    [EntityType.GlossaryNode]: 'bulkGlossaryImport',
};

/** Entity name segments used in UPDATE_<ENTITY>_<ACTION> privilege strings (sidebar + documentation) */
export const ENTITY_TYPE_NAME_MAP: Partial<Record<EntityType, string>> = {
    [EntityType.Dataset]: 'DATASET',
    [EntityType.DataProduct]: 'DATA_PRODUCT',
    [EntityType.DataFlow]: 'DATAFLOW',
    [EntityType.Dashboard]: 'DASHBOARD',
    [EntityType.Chart]: 'CHART',
    [EntityType.DataJob]: 'DATAJOB',
    [EntityType.Domain]: 'DOMAIN',
    [EntityType.Container]: 'CONTAINER',
    [EntityType.GlossaryTerm]: 'GLOSSARY_TERM',
    [EntityType.GlossaryNode]: 'GLOSSARY_NODE',
    [EntityType.Tag]: 'TAG',
};

/** Map entity type + menu action -> required privilege name (from PoliciesConfig). No entry = action not gated by RBAC here. */
export const ENTITY_TYPE_PRIVILEGE_MAP = {
    [EntityType.Dataset]: {
        DELETE: 'DELETE_DATASET',
        UPDATE_GOV_STATUS: 'UPDATE_DATASET_GOV_STATUS',
        UPDATE_GOV_FLAG_STATUS: 'UPDATE_DATASET_GOV_FLAG_STATUS',
        EDIT_READONLY: 'EDIT_READONLY',
        EDIT_DEPRECATION: 'EDIT_DATASET_DEPRECATION',
        EXPORT: 'EXPORT_DATASET',
    },
    [EntityType.GlossaryTerm]: {
        DELETE: 'DELETE_GLOSSARY_TERM',
        UPDATE_GOV_STATUS: 'UPDATE_GLOSSARY_TERM_GOV_STATUS',
        UPDATE_GOV_FLAG_STATUS: 'UPDATE_GLOSSARY_TERM_GOV_FLAG_STATUS',
        EDIT_READONLY: 'EDIT_GLOSSARY_TERM_READONLY',
        EDIT_DEPRECATION: 'EDIT_GLOSSARY_TERM_DEPRECATION',
        UPDATE_PARENT: 'UPDATE_GLOSSARY_TERM_PARENT',
        EDIT_NAME: 'UPDATE_GLOSSARY_TERM_NAME',
        CLONE: 'CLONE_GLOSSARY_TERM',
        EXPORT: 'EXPORT_GLOSSARY_TERMS',
        ADD_TERM: 'CREATE_GLOSSARY_TERM',
        ADD_TERM_GROUP: 'CREATE_GLOSSARY_NODE',
        BULK_IMPORT: 'BULK_GLOSSARY_IMPORT',
        UPDATE_SUBTYPE: 'UPDATE_GLOSSARY_TERM_SUBTYPE',

        // Related Terms
        ADD_RELATED_TERMS: 'ADD_GLOSSARY_TERM_RELATED_TERMS',
        ADD_RELATED_NODES: 'ADD_GLOSSARY_TERM_RELATED_NODES',
        REMOVE_RELATED_TERMS: 'REMOVE_GLOSSARY_TERM_RELATED_TERMS',
        REMOVE_RELATED_NODES: 'REMOVE_GLOSSARY_TERM_RELATED_NODES',
        MANAGE_CUSTOM_RELATIONSHIP: 'MANAGE_GLOSSARY_CUSTOM_RELATIONSHIP_TYPES',
        CREATE_CUSTOM_RELATIONSHIP: 'CREATE_GLOSSARY_CUSTOM_RELATIONSHIP_TYPES',
        UPDATE_CUSTOM_RELATIONSHIP: 'UPDATE_GLOSSARY_CUSTOM_RELATIONSHIP_TYPES',
        DELETE_CUSTOM_RELATIONSHIP: 'DELETE_GLOSSARY_CUSTOM_RELATIONSHIP_TYPES',
    },
    [EntityType.GlossaryNode]: {
        DELETE: 'DELETE_GLOSSARY_NODE',
        UPDATE_GOV_STATUS: 'EDIT_GLOSSARY_NODE_GOV_STATUS',
        UPDATE_GOV_FLAG_STATUS: 'UPDATE_GLOSSARY_NODE_GOV_FLAG_STATUS',
        EDIT_READONLY: 'EDIT_GLOSSARY_NODE_READONLY',
        UPDATE_PARENT: 'UPDATE_GLOSSARY_NODE_PARENT',
        EDIT_NAME: 'UPDATE_GLOSSARY_NODE_NAME',
        ADD_TERM: 'CREATE_GLOSSARY_TERM',
        ADD_TERM_GROUP: 'CREATE_GLOSSARY_NODE',
        BULK_IMPORT: 'BULK_GLOSSARY_IMPORT',
        EXPORT: 'EXPORT_GLOSSARY_TERMS',
        UPDATE_SUBTYPE: 'UPDATE_GLOSSARY_NODE_SUBTYPE',

        // Related Term Group
        ADD_RELATED_TERMS: 'ADD_GLOSSARY_NODE_RELATED_TERMS',
        ADD_RELATED_NODES: 'ADD_GLOSSARY_NODE_RELATED_NODES',
        REMOVE_RELATED_TERMS: 'REMOVE_GLOSSARY_NODE_RELATED_TERMS',
        REMOVE_RELATED_NODES: 'REMOVE_GLOSSARY_NODE_RELATED_NODES',
        MANAGE_CUSTOM_RELATIONSHIP: 'MANAGE_GLOSSARY_CUSTOM_RELATIONSHIP_TYPES',
        CREATE_CUSTOM_RELATIONSHIP: 'CREATE_GLOSSARY_CUSTOM_RELATIONSHIP_TYPES',
        UPDATE_CUSTOM_RELATIONSHIP: 'UPDATE_GLOSSARY_CUSTOM_RELATIONSHIP_TYPES',
        DELETE_CUSTOM_RELATIONSHIP: 'DELETE_GLOSSARY_CUSTOM_RELATIONSHIP_TYPES',
    },
    [EntityType.DataProduct]: {
        DELETE: 'DELETE_DATA_PRODUCT',
        UPDATE_GOV_STATUS: 'UPDATE_DATA_PRODUCT_GOV_STATUS',
        EDIT_READONLY: 'EDIT_DATA_PRODUCT_READONLY',
        EDIT_NAME: 'UPDATE_DATA_PRODUCT_NAME',
        EXPORT: 'EXPORT_DATA_PRODUCT',
    },
    [EntityType.Dashboard]: {
        DELETE: 'DELETE_DASHBOARD',
        EDIT_DEPRECATION: 'EDIT_DASHBOARD_DEPRECATION',
    },
    [EntityType.Chart]: {
        DELETE: 'DELETE_CHART',
        EDIT_DEPRECATION: 'EDIT_CHART_DEPRECATION',
    },
    [EntityType.DataFlow]: {
        DELETE: 'DELETE_DATAFLOW',
        EDIT_DEPRECATION: 'EDIT_DATAFLOW_DEPRECATION',
    },
    [EntityType.DataJob]: {
        DELETE: 'DELETE_DATAJOB',
        EDIT_DEPRECATION: 'EDIT_DATAJOB_DEPRECATION',
    },
    [EntityType.Container]: { DELETE: 'DELETE_CONTAINER' },
    [EntityType.Tag]: { DELETE: 'DELETE_TAG' },
    [EntityType.Domain]: {
        DELETE: 'DELETE_DOMAIN',
        UPDATE_PARENT: 'UPDATE_DOMAIN_PARENT',
        EDIT_NAME: 'UPDATE_DOMAIN_NAME',
        CREATE: 'CREATE_DOMAINS',
    },
    [EntityType.IngestionSource]: {
        VIEW: 'VIEW_INGESTION',
        CREATE: 'CREATE_INGESTION_SOURCE',
        UPDATE: 'UPDATE_INGESTION_SOURCE',
        DELETE: 'DELETE_INGESTION_SOURCE',
        EXECUTE: 'CREATE_INGESTION_EXECUTION_REQUEST',
        CANCEL: 'CANCEL_INGESTION_EXECUTION_REQUEST',
    },
    [SECRETS_ENTITY_TYPE]: {
        VIEW: 'VIEW_SECRETS',
        CREATE: 'CREATE_SECRET',
        DELETE: 'DELETE_SECRET',
    },
};

/** Entity actions (align with backend PoliciesConfig privilege names; used for menu, toolbar, RBAC). */
export const ENTITY_ACTIONS = {
    COPY_URL: 'COPY_URL',
    CLONE: 'CLONE',
    VIEW: 'VIEW',
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    REMOVE: 'REMOVE',
    EDIT: 'EDIT',
    DELETE: 'DELETE',
    EXECUTE: 'EXECUTE',
    CANCEL: 'CANCEL',
    UPDATE_GOV_STATUS: 'UPDATE_GOV_STATUS',
    UPDATE_GOV_FLAG_STATUS: 'UPDATE_GOV_FLAG_STATUS',
    EDIT_READONLY: 'EDIT_READONLY',
    EDIT_DEPRECATION: 'EDIT_DEPRECATION',
    UPDATE_PARENT: 'UPDATE_PARENT',
    EDIT_NAME: 'EDIT_NAME',
    EXPORT: 'EXPORT',
    ADD_TERM: 'ADD_TERM',
    ADD_TERM_GROUP: 'ADD_TERM_GROUP',
    BULK_IMPORT: 'BULK_IMPORT',
    UPDATE_SUBTYPE: 'UPDATE_SUBTYPE',
    LINEAGE: 'LINEAGE',
    MANAGE_CUSTOM_RELATIONSHIP: 'MANAGE_CUSTOM_RELATIONSHIP',
    CREATE_CUSTOM_RELATIONSHIP: 'CREATE_CUSTOM_RELATIONSHIP',
    UPDATE_CUSTOM_RELATIONSHIP: 'UPDATE_CUSTOM_RELATIONSHIP',
    DELETE_CUSTOM_RELATIONSHIP: 'DELETE_CUSTOM_RELATIONSHIP',
    ADD_RELATED_TERMS: 'ADD_RELATED_TERMS',
    ADD_RELATED_NODES: 'ADD_RELATED_NODES',
    REMOVE_RELATED_TERMS: 'REMOVE_RELATED_TERMS',
    REMOVE_RELATED_NODES: 'REMOVE_RELATED_NODES',
    TAGS: 'TAGS',
    GLOSSARY_TERMS: 'GLOSSARY_TERMS',
    DOMAINS: 'DOMAINS',
    DATA_PRODUCTS: 'DATA_PRODUCTS',
    OWNERS: 'OWNERS',
    DOC_LINKS: 'DOC_LINKS',
    DOCS: 'DOCS',
} as const;

/** Action keys for related terms/nodes (subset of ENTITY_ACTIONS; used with ENTITY_TYPE_PRIVILEGE_MAP). */
export type RelatedTermPrivilegeActionKey = keyof Pick<
    typeof ENTITY_ACTIONS,
    'ADD_RELATED_TERMS' | 'ADD_RELATED_NODES' | 'REMOVE_RELATED_TERMS' | 'REMOVE_RELATED_NODES'
>;

/** Resolved set of boolean flags for the Ingestion page. */
export interface IngestionPrivileges {
    canView: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    canExecute: boolean;
    canCancel: boolean;
}

/** Resolved set of boolean flags for the Secrets page (PoliciesConfig.SECRET_PRIVILEGES). */
export interface SecretPrivileges {
    canView: boolean;
    canCreate: boolean;
    canDelete: boolean;
}

/** Resolved set of boolean flags for the Ownership Types page (PoliciesConfig.OWNERSHIP_TYPE_PRIVILEGES). */
export interface OwnershipTypePrivileges {
    canView: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
}

/** Schema/column-level actions (EDIT_<ENTITY>_COL_<ACTION> per PoliciesConfig) */
export const SCHEMA_COL_ACTIONS = {
    TAGS: 'TAGS',
    GLOSSARY_TERMS: 'GLOSSARY_TERMS',
    DESCRIPTION: 'DESCRIPTION',
    GOVERNANCE_FLAGS: 'GOV_FLAG',
    DOMAINS: 'DOMAIN',
};

/** Privilege name segments used in <ACTION>_<ENTITY>_<PRIVILEGE_NAME> strings */
export const PRIVILEGE_NAMES = {
    STRUCTURED_PROPERTIES: 'STRUCTURED_PROPERTIES',
    CUSTOM_PROPERTIES: 'CUSTOM_PROPERTIES',
    AUDIT: 'AUDIT',
} as const;

/** Ownership type fine-grained privilege strings (PoliciesConfig.OWNERSHIP_TYPE_PRIVILEGES) */
export const OWNERSHIP_TYPE_PRIVILEGE_STRINGS = {
    VIEW: 'VIEW_OWNERSHIP_TYPES',
    CREATE: 'CREATE_OWNERSHIP_TYPE',
    UPDATE: 'UPDATE_OWNERSHIP_TYPE',
    DELETE: 'DELETE_OWNERSHIP_TYPE',
} as const;
